import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toSlug } from '../../../shared/utils/formatters';

const DEFAULT_IMG = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=150&h=150&fit=crop';
const COLLAGE_DEFAULT = 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&h=400&fit=crop';

const COLLAGE_LAYOUT = [
  { transform: 'rotate(-3deg) translateY(10px)',  zIndex: 2 },
  { transform: 'rotate(4deg) translateY(30px)',   zIndex: 1 },
  { transform: 'rotate(-4deg) translateY(-10px)', zIndex: 3 },
  { transform: 'rotate(2deg) translateY(10px)',   zIndex: 4 },
];

const heroShimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const HeroSection = styled.section`
  background-color: #fcfcfc;
  width: 100%;
  overflow: hidden;
  padding-bottom: 0px;
`;

const HeroContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px 20px;
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: 991px) {
    flex-direction: column;
    padding: 28px 16px 16px;
    gap: 24px;
  }

  @media (max-width: 600px) {
    padding: 20px 12px 12px;
  }
`;

const HeroLeft = styled.div`
  flex: 0 0 55%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 991px) {
    flex: 0 0 100%;
    width: 100%;
  }
`;

const HeroRight = styled.div`
  flex: 0 0 43%;
  position: relative;
  height: 520px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 16px;
  padding: 16px;

  @media (max-width: 1200px) {
    height: 460px;
  }

  @media (max-width: 991px) {
    flex: 0 0 100%;
    width: 100%;
    display: none;
  }
`;

const HeroTitle = styled.h1`
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 800;
  color: #111827;
  line-height: 1.15;
  margin-bottom: 14px;
  letter-spacing: -0.5px;
`;

const TitleAccent = styled.span`
  color: #1e3a8a;
`;

const ServiceBox = styled.div`
  border: 1px solid #eaeaea;
  border-radius: 20px;
  padding: 20px;
  background: #ffffff;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.03);

  @media (max-width: 991px) {
    padding: 16px;
  }
`;

const ServiceBoxHeading = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #334155;
  margin: 0;
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 16px;

  @media (max-width: 991px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
  }

  /* Mobile: 3 services per row. */
  @media (max-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  @media (max-width: 400px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }
`;

const ServiceTag = styled.div`
  background: #ffffff;
  color: #475569;
  padding: 3px 8px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: 600;
  border: 1px solid #e2e8f0;
  transition: opacity 0.3s ease;

  @media (max-width: 600px) {
    font-size: 8px;
    padding: 2px 6px;
  }
`;

const BookNowBtn = styled.div`
  position: absolute;
  bottom: 8px;
  background: #082189;
  color: #ffffff;
  font-size: 10px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  opacity: 0;
  transform: translateY(6px);
  transition: all 0.3s ease;
`;

const ServiceTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #1e293b;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 6px;
  transition: opacity 0.3s ease;

  @media (max-width: 600px) {
    font-size: 10px;
  }

  @media (max-width: 400px) {
    font-size: 9px;
  }
`;

const ServiceItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 12px 8px;
  background: #ffffff;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  border: 1px solid #f1f5f9;

  &:hover {
    background: #f8fafc;
    border-color: #e2e8f0;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
    transform: translateY(-3px);
  }

  img {
    width: 77px;
    height: 77px;
    border-radius: 12px;
    object-fit: cover;
    margin-bottom: 8px;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.08);
  }

  &:hover ${ServiceTag} {
    opacity: 0;
  }

  &:hover ${BookNowBtn} {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 600px) {
    padding: 10px 4px;

    img {
      width: 44px;
      height: 44px;
      border-radius: 10px;
      margin-bottom: 6px;
    }
  }

  @media (max-width: 400px) {
    img {
      width: 38px;
      height: 38px;
    }
  }
`;


const HeroSkel = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${heroShimmer} 1.4s infinite;
  border-radius: 8px;
`;

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  margin-top: 20px;
  padding-left: 4px;

  @media (max-width: 600px) {
    gap: 24px;
    margin-top: 16px;
  }
`;

const CollageCard = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  transition: transform 0.4s ease;

  &:hover {
    transform: scale(1.05) rotate(0deg) translateY(0) !important;
    z-index: 10 !important;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CollageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.7), transparent);
`;

const CollageLabel = styled.div`
  position: absolute;
  bottom: 14px;
  left: 14px;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
`;


const Hero = ({ categories = [], loading = false }) => {
  const navigate = useNavigate();

  const tiles = categories.map(cat => {
    const name = cat.categoryName ?? cat.name ?? '';
    const img = cat.imageURL || cat.imagePath
      || cat.subCategories?.[0]?.imageURL || cat.subCategories?.[0]?.imagePath
      || cat.subcategories?.[0]?.imageURL || cat.subcategories?.[0]?.imagePath
      || DEFAULT_IMG;
    return { label: name, slug: toSlug(name), img };
  });

  // Collage: first 4 categories, falls back to layout defaults if fewer
  const collageImages = COLLAGE_LAYOUT.map((layout, i) => {
    const cat = categories[i];
    return {
      src: cat ? (cat.imageURL || cat.imagePath || cat.bannerURL || COLLAGE_DEFAULT) : COLLAGE_DEFAULT,
      title: cat ? (cat.categoryName ?? cat.name ?? '') : '',
      transform: layout.transform,
      zIndex: layout.zIndex,
    };
  });

  return (
    <HeroSection>
      <HeroContainer className="hero-container">
        <HeroRight className="hero-right">
          {collageImages.map((image, index) => (
            <CollageCard
              key={index}
              className="collage-card"
              style={{ transform: image.transform, zIndex: image.zIndex }}
            >
              <img src={image.src} alt={image.title} />
              <CollageOverlay className="collage-overlay" />
              <CollageLabel className="collage-label">{image.title}</CollageLabel>
            </CollageCard>
          ))}
        </HeroRight>

        <HeroLeft className="hero-left">
          <HeroTitle>
            Trusted Home Care,
            Premium <br /> Services at
            Your <TitleAccent>Doorstep.</TitleAccent>
          </HeroTitle>

          <ServiceBox className="service-box">
            <ServiceBoxHeading>
              What are you looking for?
            </ServiceBoxHeading>

            <ServiceGrid className="service-grid">
              {loading
                ? Array.from({ length: 8 }).map((_, i) => (
                    <ServiceItem key={i} className="service-item" style={{ pointerEvents: 'none' }}>
                      <HeroSkel className="hero-skel" style={{ width: 54, height: 54, borderRadius: 12, marginBottom: 8 }} />
                      <HeroSkel className="hero-skel" style={{ height: 9, width: 52, borderRadius: 4 }} />
                    </ServiceItem>
                  ))
                : tiles.map((item, index) => (
                    <ServiceItem
                      key={item.slug || index}
                      className="service-item"
                      onClick={() => navigate(`/${item.slug}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={e => e.key === 'Enter' && navigate(`/${item.slug}`)}
                    >
                      <img
                        src={item.img}
                        alt={item.label}
                        onError={e => { e.target.src = DEFAULT_IMG; }}
                      />
                      <ServiceTitle className="service-title">{item.label}</ServiceTitle>
                      <ServiceTag className="service-tag">★ Top Rated</ServiceTag>
                      <BookNowBtn className="book-now-btn">Explore →</BookNowBtn>
                    </ServiceItem>
                  ))
              }
            </ServiceGrid>
          </ServiceBox>

          <StatsRow className="stats-row">
            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <div style={{ fontSize: '28px' }}>⭐</div>
              <div>
                <div style={{ fontSize: '22px', fontWeight: '800', color: '#111827' }}>4.8</div>
                <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>Service Rating*</div>
              </div>
            </div> */}

            {/* <StatItem>
              <StatEmoji>👥</StatEmoji>
              <div>
                <StatValue>12M+</StatValue>
                <StatLabel>Customers Globally*</StatLabel>
              </div>
            </StatItem> */}
          </StatsRow>
        </HeroLeft>
      </HeroContainer>
    </HeroSection>
  );
};

export default Hero;
